import { useEffect, useState } from "react";

/**
 * Drives the two-phase enter/exit animation used by our right-hand slide-in
 * panels (booking detail, contact detail, …).
 *
 * Why two flags:
 *  - `shouldRender` keeps the panel mounted during BOTH the open and the close
 *    animation. It flips on immediately when opening, and off only AFTER the
 *    close animation has finished, so the exit transition is actually visible.
 *  - `isVisible` drives the CSS transition target (translate-x / opacity). On
 *    open we flip it on the NEXT paint (double requestAnimationFrame) so the
 *    element is first committed in its off-screen state, then transitions in —
 *    without the rAF the browser can batch both states and the panel just
 *    "appears" with no glide.
 *
 * This mirrors the timing of CreateMeetingWizard so all slide-ins feel the same
 * (500ms, ease-out). Pair it with `transition duration-500 ease-out` on the
 * panel and `transition-opacity duration-500` on the backdrop.
 *
 * @param isOpen   Whether the panel should be shown.
 * @param closeMs  How long the close animation runs, in ms (default 500). Must
 *                 match the CSS transition duration so the unmount waits for it.
 */
export function useSlidePanel(isOpen: boolean, closeMs = 500) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Defer the visible state to the next paint (twice) so the off-screen
      // starting position is committed before the transition begins.
      let innerRaf = 0;
      const outerRaf = requestAnimationFrame(() => {
        innerRaf = requestAnimationFrame(() => setIsVisible(true));
      });
      return () => {
        cancelAnimationFrame(outerRaf);
        cancelAnimationFrame(innerRaf);
      };
    }

    // Closing: start the exit transition, then unmount once it has finished.
    setIsVisible(false);
    const timer = setTimeout(() => setShouldRender(false), closeMs);
    return () => clearTimeout(timer);
  }, [isOpen, closeMs]);

  return { isVisible, shouldRender };
}
